/**
 * Global Ayurveda — product page behaviour.
 *
 * Deliberately small: the theme already owns the gallery, variant picker,
 * cart and sticky add-to-cart. This file only adds what the new sections need.
 * Loaded as a module, so repeated <script> tags on the page execute it once.
 */
import { StandardEvents } from '@shopify/events';

/**
 * <ga-pdp-live data-key="…">
 * Re-syncs server-rendered buy-box fragments (Sale badge, savings line) when
 * the variant changes. The theme's variant picker already fetches the
 * section's fresh HTML and exposes it on the productSelect event; this copies
 * the matching fragment out of that response, so no extra request is made.
 */
class GaPdpLive extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
  }

  #onSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    event.promise
      ?.then(({ detail }) => {
        const fresh = detail?.html?.querySelector(`ga-pdp-live[data-key="${CSS.escape(this.dataset.key)}"]`);
        if (fresh) this.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  };
}

/**
 * <ga-pdp-tabs>
 * Desktop: WAI-ARIA tabs (arrow keys, Home/End, roving tabindex).
 * Mobile: each panel's toggle button works as an accordion.
 * CSS decides which controls are visible; `data-ready` is only set once the
 * behaviour is attached, so without JS every panel stays readable.
 */
class GaPdpTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = this.tabs.map((tab) => this.querySelector(`#${CSS.escape(tab.getAttribute('aria-controls'))}`));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.#onKey(event, index));
    });

    this.querySelectorAll('.ga-pdp-tabs__toggle').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        toggle.closest('.ga-pdp-tabs__panel')?.toggleAttribute('data-open', open);
      });
    });

    this.setAttribute('data-ready', '');
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      this.panels[i]?.toggleAttribute('data-active', active);
    });
    if (focus) this.tabs[index]?.focus();
  }

  #onKey(event, index) {
    const last = this.tabs.length - 1;
    const next = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    this.select(next, true);
  }
}

if (!customElements.get('ga-pdp-live')) customElements.define('ga-pdp-live', GaPdpLive);
if (!customElements.get('ga-pdp-tabs')) customElements.define('ga-pdp-tabs', GaPdpTabs);

/** "Show all reviews": reveal the remaining cards and move focus to the first one. */
document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('[data-ga-show-all]') : null;
  if (!button) return;
  const root = button.closest('[data-ga-show-root]');
  const hidden = root ? [...root.querySelectorAll('[data-ga-extra]')] : [];
  hidden.forEach((el) => {
    el.hidden = false;
    el.removeAttribute('data-ga-extra');
  });
  if (hidden[0]) {
    hidden[0].tabIndex = -1;
    hidden[0].focus({ preventScroll: true });
  }
  button.closest('.ga-pdp-center')?.remove();
});
